import React, { useEffect, useState, useContext } from "react";
import axios from "../api/axios";
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from "@mui/material";
import { Edit } from "@mui/icons-material";
import CommonTable from "../components/CommonTable";
import { useMessage } from "../context/MessageContext";
import { useConfirm } from "../context/ConfirmContext";
import { AuthContext } from "../context/AuthContext";

const YearFormDialog = ({ open, onClose, onSave, year }) => {
  const [form, setForm] = useState(year || { name: "", start: "", end: "" });

  useEffect(() => {
    setForm(year || { name: "", start: "", end: "" });
  }, [year]);

  const handleChange = (e) =>
    setForm({ ...form, [e.target.name]: e.target.value });
  const handleSubmit = () => onSave(form);

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>{year ? "Edit" : "Add"} Academic Year</DialogTitle>
      <DialogContent>
        <TextField
          label="Year Name"
          name="name"
          value={form.name}
          onChange={handleChange}
          fullWidth
          margin="normal"
          required
          placeholder="e.g. AY2024-25"
        />
        <TextField
          label="Start Date"
          name="start"
          type="date"
          value={form.start ? form.start.substring(0, 10) : ""}
          onChange={handleChange}
          fullWidth
          margin="normal"
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          label="End Date"
          name="end"
          type="date"
          value={form.end ? form.end.substring(0, 10) : ""}
          onChange={handleChange}
          fullWidth
          margin="normal"
          InputLabelProps={{ shrink: true }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSubmit}>
          {year ? "Update" : "Add"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const AcademicYears = () => {
  const { user } = useContext(AuthContext);
  const { showMessage } = useMessage();
  const { confirm } = useConfirm();

  const [years, setYears] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editingYear, setEditingYear] = useState(null);

  const fetchYears = async () => {
    try {
      setLoading(true);
      const res = await axios.get("/academic-years");
      setYears(res.data);
    } catch (err) {
      console.error(err);
      showMessage("Failed to load academic years", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchYears();
  }, []);

  const handleAdd = () => {
    setEditingYear(null);
    setOpen(true);
  };

  const handleEdit = (year) => {
    setEditingYear(year);
    setOpen(true);
  };

  const handleSave = async (form) => {
    try {
      if (editingYear) {
        await axios.put(`/academic-years/${editingYear._id}`, form);
        showMessage("Academic year updated", "success");
      } else {
        await axios.post("/academic-years", form);
        showMessage("Academic year added", "success");
      }
      setOpen(false);
      fetchYears();
    } catch (err) {
      console.error(err);
      showMessage("Failed to save academic year", "error");
    }
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Academic Years
      </Typography>

      {user.role === "super_admin" && (
        <Button variant="contained" onClick={handleAdd} sx={{ mb: 2 }}>
          Add Academic Year
        </Button>
      )}

      <CommonTable
        columns={[
          { label: "Year Name", field: "name" },
          {
            label: "Start Date",
            field: "start",
            renderCell: (y) => y.start?.substring(0, 10) || "-",
          },
          {
            label: "End Date",
            field: "end",
            renderCell: (y) => y.end?.substring(0, 10) || "-",
          },
          {
            label: "Actions",
            sortable: false,
            renderCell: (year) =>
              user.role === "super_admin" && (
                <Button
                  size="small"
                  onClick={() => handleEdit(year)}
                  startIcon={<Edit />}
                >
                  Edit
                </Button>
              ),
          },
        ]}
        rows={years}
        rowsPerPageOptions={[5, 10, 25]}
      />

      <YearFormDialog
        open={open}
        onClose={() => setOpen(false)}
        onSave={handleSave}
        year={editingYear}
      />
    </Box>
  );
};

export default AcademicYears;
